import React, { useEffect, useState } from 'react';
import { MessageSquare, Send, ThumbsUp, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { mockAppraisals } from '../../../data/mockData';
import { formatDate } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { useFeedbacks, useNotifications, useUsers } from '../../../hooks/useDatabase';

export default function FeedbackPage() {
  const { user } = useAuthStore();
  const { getUsers } = useUsers();
  const { createFeedback, getFeedbacksForUser, loading: feedbackLoading } = useFeedbacks();
  const { createNotification } = useNotifications();
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [recipientRole, setRecipientRole] = useState('supervisor');
  const [anonymous, setAnonymous] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [supervisorUser, setSupervisorUser] = useState(null);
  const [ceoUser, setCeoUser] = useState(null);
  const [directFeedback, setDirectFeedback] = useState([]);

  // Get feedback from completed appraisals
  const myAppraisals = mockAppraisals.filter(a => 
    (a.userId === user?.id || a.userId === '3') && a.status === 'finalized'
  );

  useEffect(() => {
    (async () => {
      const users = await getUsers();
      setAdminUser(users.find(u => u.role === 'admin') || null);
      setCeoUser(users.find(u => u.role === 'ceo') || null);
      const directSupervisor = users.find(u => u.id === user?.supervisorId);
      setSupervisorUser(directSupervisor || users.find(u => u.role === 'supervisor') || null);
    })();
  }, [getUsers, user]);

  const supervisor = supervisorUser;

  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      const items = await getFeedbacksForUser(user.id);
      setDirectFeedback(items || []);
    })();
  }, [getFeedbacksForUser, user]);

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) {
      toast.error('Please enter your feedback');
      return;
    }
    if (!recipientRole) {
      toast.error('Please choose who to send this to');
      return;
    }

    const targetUser = recipientRole === 'admin' ? adminUser : recipientRole === 'ceo' ? ceoUser : supervisorUser;
    if (!targetUser) {
      toast.error('Recipient not available');
      return;
    }

    try {
      await createFeedback({
        senderId: user?.id,
        senderRole: user?.role,
        receiverId: targetUser.id,
        receiverRole: recipientRole,
        anonymous: recipientRole === 'supervisor' ? anonymous : false,
        type: feedbackType || 'general',
        message: feedbackText.trim(),
        department: user?.profile?.department || ''
      });

      await createNotification({
        userId: targetUser.id,
        type: 'feedback_received',
        title: 'New feedback received',
        message: recipientRole === 'supervisor' && anonymous
          ? 'Anonymous feedback from your team'
          : `Feedback from ${user?.profile?.firstName || 'Employee'}`,
        link: recipientRole === 'admin' ? '/admin/feedback' : recipientRole === 'ceo' ? '/ceo/feedback' : '/supervisor/feedback'
      });

      toast.success('Feedback submitted successfully');
      setFeedbackText('');
      setFeedbackType('');
      setAnonymous(false);
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Feedback</h1>
        <p className="text-gray-500 mt-1">View received feedback and submit new feedback</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Submit Feedback */}
        <Card className="border-0 shadow-sm lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Send className="w-5 h-5 text-cyan-500" />
              Submit Feedback
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Send To</Label>
                <Select value={recipientRole} onValueChange={setRecipientRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose recipient" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="ceo">CEO</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Feedback Type</Label>
                <Select value={feedbackType} onValueChange={setFeedbackType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="suggestion">Suggestion</SelectItem>
                    <SelectItem value="issue">Report Issue</SelectItem>
                    <SelectItem value="appreciation">Appreciation</SelectItem>
                    <SelectItem value="general">General Feedback</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {recipientRole === 'supervisor' && (
              <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">Send anonymously</p>
                  <p className="text-xs text-gray-500">Hide your name from your supervisor</p>
                </div>
                <Switch checked={anonymous} onCheckedChange={setAnonymous} />
              </div>
            )}
            <div className="space-y-2">
              <Label>Your Feedback</Label>
              <Textarea
                placeholder="Share your thoughts..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                className="min-h-32"
              />
            </div>
            <Button 
              onClick={handleSubmitFeedback}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
              disabled={feedbackLoading}
            >
              <Send className="w-4 h-4 mr-2" /> {feedbackLoading ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </CardContent>
        </Card>

        {/* Direct Feedback */}
        <div className="space-y-4 lg:col-span-2">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-teal-500" />
                Direct Feedback to You
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {directFeedback.length === 0 && (
                <p className="text-sm text-gray-500">No direct feedback yet</p>
              )}
              {directFeedback.map(item => (
                <div key={item.id} className="p-3 border border-gray-100 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{item.anonymous ? 'Anonymous' : 'From your manager/admin'}</p>
                      {item.department && <p className="text-xs text-gray-500">{item.department}</p>}
                    </div>
                    <Badge variant="secondary" className="capitalize">{item.type}</Badge>
                  </div>
                  <p className="text-gray-700 mt-2 whitespace-pre-line">{item.message}</p>
                  <p className="text-xs text-gray-400 mt-2">{new Date(item.createdAt).toLocaleString()}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Received Feedback (Appraisals) */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-violet-500" />
                Appraisal Feedback
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myAppraisals.length > 0 ? (
                <div className="space-y-4">
                  {myAppraisals.map(appraisal => (
                    <div key={appraisal.id}>
                      {appraisal.performanceAreas?.filter(a => a.managerComment).map((area, i) => (
                        <div key={i} className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg mb-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {supervisor?.profile?.firstName?.[0]}{supervisor?.profile?.lastName?.[0]}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {supervisor?.profile?.firstName} {supervisor?.profile?.lastName}
                                </p>
                                <p className="text-xs text-gray-500">Manager</p>
                              </div>
                            </div>
                            <Badge variant="secondary">{area.competencyName}</Badge>
                          </div>
                          <p className="text-gray-700">{area.managerComment}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className="bg-violet-100 text-violet-700">Score: {area.managerScore}/5</Badge>
                            <span className="text-xs text-gray-400">{appraisal.title}</span>
                          </div>
                        </div>
                      ))}

                      {appraisal.comments?.manager && (
                        <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg mb-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {supervisor?.profile?.firstName?.[0]}{supervisor?.profile?.lastName?.[0]}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {supervisor?.profile?.firstName} {supervisor?.profile?.lastName}
                                </p>
                                <p className="text-xs text-gray-500">Manager</p>
                              </div>
                            </div>
                            <Badge className="bg-emerald-100 text-emerald-700">Overall</Badge>
                          </div>
                          <p className="text-gray-700">{appraisal.comments.manager}</p>
                          <p className="text-xs text-gray-400 mt-2">From: {appraisal.title} • {formatDate(appraisal.finalizedAt)}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No appraisal feedback yet</p>
                  <p className="text-sm text-gray-400 mt-1">Feedback will appear here after appraisals are completed</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Actions */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div 
              className="p-4 border border-gray-100 rounded-lg hover:border-emerald-200 hover:bg-emerald-50/50 cursor-pointer transition-colors"
              onClick={() => { setFeedbackType('appreciation'); toast.info('Select Appreciation and write your message'); }}
            >
              <ThumbsUp className="w-8 h-8 text-emerald-500 mb-2" />
              <h4 className="font-medium text-gray-900">Send Appreciation</h4>
              <p className="text-sm text-gray-500">Recognize a colleague's work</p>
            </div>
            <div 
              className="p-4 border border-gray-100 rounded-lg hover:border-amber-200 hover:bg-amber-50/50 cursor-pointer transition-colors"
              onClick={() => { setFeedbackType('suggestion'); toast.info('Select Suggestion and share your idea'); }}
            >
              <MessageSquare className="w-8 h-8 text-amber-500 mb-2" />
              <h4 className="font-medium text-gray-900">Make a Suggestion</h4>
              <p className="text-sm text-gray-500">Share improvement ideas</p>
            </div>
            <div 
              className="p-4 border border-gray-100 rounded-lg hover:border-rose-200 hover:bg-rose-50/50 cursor-pointer transition-colors"
              onClick={() => { setFeedbackType('issue'); toast.info('Select Report Issue and describe the problem'); }}
            >
              <AlertCircle className="w-8 h-8 text-rose-500 mb-2" />
              <h4 className="font-medium text-gray-900">Report an Issue</h4>
              <p className="text-sm text-gray-500">Flag problems or concerns</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
